import {ImageService} from '../image.service';
import {Food, IFood, IMealFood, isIFood, isIMealFood, IUnit, MealFood, Unit} from './food';
import {
  FoodProduct,
  IFoodProduct,
  IMealFoodProduct,
  isIFoodProduct,
  isIMealFoodProduct,
  MealFoodProduct
} from './FoodProduct';
import {IMealRecipe, IRecipe, isIMealRecipe, isIRecipe, MealRecipe, Recipe} from './Recipe';
import {CategoryStatistics, SingleCategoryStatistic} from './Statistics';
import {roundDecimals} from '../helpers/helpers';

export class MealItem {

  amount: number;
  unit: Unit;
  type: MealItemType;
  private _recipeImgUrl: string;

  constructor(i: Food | Recipe | FoodProduct | MealFood | MealRecipe | MealFoodProduct | IMealItem) {
    if (i instanceof Food || i instanceof Recipe || i instanceof FoodProduct) {
      this._originalItem = i;
      this.initGeneralItems(i);
    } else if (i instanceof MealFood || i instanceof MealRecipe || i instanceof MealFoodProduct) {
      if (i instanceof MealFood) {
        this._originalItem = i.food;
      }
      if (i instanceof MealRecipe) {
        this._originalItem = i.recipe;
      }
      if (i instanceof MealFoodProduct) {
        this._originalItem = i.foodProduct;
      }
      this.initMealItems(i);
    } else {
      if (isIFood(i.originalItem)) {
        this._originalItem = new Food(i.originalItem);
        this.type = MealItemType.food;
      }
      if (isIRecipe(i.originalItem)) {
        this._originalItem = new Recipe(i.originalItem);
        this.type = MealItemType.recipe;
      }
      if (isIFoodProduct(i.originalItem)) {
        this._originalItem = new FoodProduct(i.originalItem);
        this.type = MealItemType.foodProduct;
      }
      this.amount = i.amount;
      this.unit = new Unit(i.unit);
    }
  }

  get name(): string {
    return this.originalItem.name;
  }

  get nameWithoutBrand(): string {
    return (this.originalItem instanceof FoodProduct) ?
        this.originalItem.nameWithoutBrand : this.originalItem.name;
  }

  get img(): string {
    if (this.originalItem instanceof Recipe) {
      return this._recipeImgUrl ? this._recipeImgUrl : this.originalItem.imgUrl;
    }
    return this.originalItem.imgUrl;
  }

  get emissions(): number {
    if (this.originalItem instanceof Recipe) {
      return this.originalItem.emissionsPerPortion * this.amount;
    }
    return this.calculateFoodEmissions();
  } // Emissions ins grams

  get calories(): number {
    if (this.originalItem instanceof Recipe) {
      return this.originalItem.caloriesPerPortion * this.amount;
    }
    return this.calculateFoodCalories();
  }

  get carbsKnown(): boolean {
    return this.originalItem.carbsKnown;
  }

  get carbs(): number {
    return this.carbsKnown ? (this.originalItem.carbsPer100g * this.amountIn100g) : 0;
  }

  get fatKnown(): boolean {
    return this.originalItem.fatKnown;
  }

  get fat(): number {
    return this.fatKnown ? (this.originalItem.fatPer100g * this.amountIn100g) : 0;
  }

  get proteinKnown(): boolean {
    return this.originalItem.proteinKnown;
  }

  get protein(): number {
    return this.proteinKnown ? (this.originalItem.proteinPer100g * this.amountIn100g) : 0;
  }

  get macrosKnown(): boolean {
    return this.carbsKnown && this.proteinKnown && this.fatKnown;
  }

  get amountInGram(): number {
    if (this.originalItem instanceof Recipe) {
      return this.amount * this.originalItem.amountInGram / this.originalItem.portions;
    }
    return this.unit.toGram(this.amount);
  }

  get amountWithUnit(): string {
    switch (this.type) {
      case MealItemType.recipe: {
        if (this.amount === 1) {
          return this.amount + ' Portion';
        }
        return this.amount + ' Portionen';
      }
      default: {
        if (this.unit.name === 'g' || this.unit.name === 'ml' || this.unit.name === 'cl' || this.unit.name === 'l') {
          return this.amount + this.unit.name;
        }
        if (this.unit.name === 'Packung' || this.unit.name === 'Portion') {
          return this.amount + ' ' + this.unit.name;
        }
        return this.amount + ', ' + this.unit.name;
      }
    }
  }

  get categoryStatisticsEmissions(): SingleCategoryStatistic | CategoryStatistics {
    const detailedItem = this.toDetailedMealItem();
    if (detailedItem instanceof MealRecipe) {
      return detailedItem.categoryStatisticsEmissions;
    }
    return detailedItem.categoryStatisticEmissions;
  }

  get categoryStatisticsCalories(): SingleCategoryStatistic | CategoryStatistics {
    const detailedItem = this.toDetailedMealItem();
    if (detailedItem instanceof MealRecipe) {
      return detailedItem.categoryStatisticsCalories;
    }
    return detailedItem.categoryStatisticCalories;
  }

  private _originalItem: Food | Recipe | FoodProduct;
  get originalItem(): Food | Recipe | FoodProduct {
    return this._originalItem;
  }

  private get amountIn100g(): number {
    return this.amountInGram / 100;
  }

  public static deepCopyArray(mealItems: MealItem[]): MealItem[] {
    const ret: MealItem[] = [];
    mealItems.forEach(element => {
      ret.push(element.deepCopy());
    });
    return ret;
  }

  public static fromDetailedInterface(i: IMealFood | IMealRecipe | IMealFoodProduct): MealItem {
    if (isIMealFood(i)) {
      return new MealItem(new MealFood(i));
    }
    if (isIMealRecipe(i)) {
      return new MealItem(new MealRecipe(i));
    }
    if (isIMealFoodProduct(i)) {
      return new MealItem(new MealFoodProduct(i));
    }
  }

  public static fromDetailedInterfaceArray(i: (IMealFood | IMealRecipe | IMealFoodProduct)[]): MealItem[] {
    const ret: MealItem[] = [];
    if (!i || i.length === 0) {
      return ret;
    }
    i.forEach(item => ret.push(this.fromDetailedInterface(item)));
    return ret;
  }

  public static toDetailedMealItemInterfaceArray(items: MealItem[]): (IMealFood | IMealRecipe | IMealFoodProduct)[] {
    const ret: (IMealFood | IMealRecipe | IMealFoodProduct)[] = [];
    items.forEach(item => ret.push(item.toDetailedMealItemInterface()));
    return ret;
  }

  public updateMacros(foods: Food[], foodProducts: FoodProduct[]): void {
    if (this._originalItem instanceof Recipe) {
      this._originalItem.updateMacros(foods, foodProducts);
      return;
    }
    if (this._originalItem instanceof Food) {
      const usedFood = foods.find(f => f.id === this._originalItem.id);
      if (!usedFood || !usedFood.macrosKnown) {
        return;
      }
      this.updateMacrosByValue(usedFood.carbsPer100g, usedFood.fatPer100g, usedFood.proteinPer100g);
    } else {
      const usedFoodProduct = foodProducts.find(f => f.id === this._originalItem.id);
      if (!usedFoodProduct || !usedFoodProduct.macrosKnown) {
        return;
      }
      this.updateMacrosByValue(usedFoodProduct.carbsPer100g, usedFoodProduct.fatPer100g, usedFoodProduct.proteinPer100g);
    }
  }

  public toDetailedMealItem(): MealFood | MealRecipe | MealFoodProduct {
    if (this.originalItem instanceof Food) {
      const mealFood: MealFood = new MealFood(this.originalItem);
      mealFood.amount = this.amount;
      mealFood.unit = this.unit;
      return mealFood;
    }
    if (this.originalItem instanceof Recipe) {
      const mealRecipe: MealRecipe = new MealRecipe(this.originalItem);
      mealRecipe.amount = this.amount;
      return mealRecipe;
    }
    if (this.originalItem instanceof FoodProduct) {
      const mealFoodProduct: MealFoodProduct = new MealFoodProduct(this.originalItem);
      mealFoodProduct.amount = this.amount;
      mealFoodProduct.unit = this.unit;
      return mealFoodProduct;
    }
  }

  public toDetailedMealItemInterface(): IMealFood | IMealRecipe | IMealFoodProduct {
    return this.toDetailedMealItem().toInterface();
  }

  public toInterface(): IMealItem {
    const unit = this.unit ? this.unit.toInterface() : undefined;
    return {amount: this.amount, unit, type: this.type, originalItem: this.originalItem.toInterface()};
  }

  public deepCopy(): MealItem {
    return new MealItem(this.toInterface());
  }

  public equals(item: MealItem): boolean {
    if (this.unit === undefined && item.unit !== undefined || this.unit !== undefined && item.unit === undefined) {
      return false;
    }
    if (this.amount === item.amount && (!this.unit || this.unit.equals(item.unit)) && this.originalItem.id === item.originalItem.id) {
      return true;
    }
    return false;
  }

  // Problem is that original item doesn't have it but is readonly and cannot be changed
  public fetchRecipeImage(imageService: ImageService): void {
    if (this.type !== MealItemType.recipe) {
      return;
    }
    // TODO: Adjust to idoplanty recipes
    imageService.getRecipeUserImage(this.originalItem.id).subscribe({
      next: id => this._recipeImgUrl = id
    });
  }

  // Method just for recipe meal items which returns the ingredients for a recipe to ingredients breakdown
  public toIndividualIngredients(): MealItem[] {
    if (this.type !== MealItemType.recipe) {
      return;
    }
    const mealItems: MealItem[] = [];
    (this.originalItem as Recipe).ingredients.forEach(ingredient => {
      const mealItem = new MealItem(ingredient);
      mealItem.amount = roundDecimals(mealItem.amount * this.amount / (this.originalItem as Recipe).portions, 2);
      mealItems.push(mealItem);
    });
    return mealItems;
  }

  private initGeneralItems(i: Food | Recipe | FoodProduct): void {
    if (i instanceof Food || i instanceof FoodProduct) {
      this.amount = 0;
      // First unit in possible units array is the default unit
      if (i.possibleUnits && i.possibleUnits.length > 0) {
        this.unit = i.possibleUnits[0];
      }
      if (i instanceof Food) {
        this.type = MealItemType.food;
      }
      if (i instanceof FoodProduct) {
        this.type = MealItemType.foodProduct;
      }
    } else {
      this.amount = 1;
      this.type = MealItemType.recipe;
      this.unit = i.unit;
    }
  }

  private initMealItems(i: MealFood | MealRecipe | MealFoodProduct): void {
    this.amount = i.amount;
    this.unit = i.unit;
    if (i instanceof MealFood) {
      this.type = MealItemType.food;
    }
    if (i instanceof MealRecipe) {
      this.type = MealItemType.recipe;
    }
    if (i instanceof MealFoodProduct) {
      this.type = MealItemType.foodProduct;
    }
  }

  private calculateFoodCalories(): number {
    if (this.originalItem instanceof Recipe) {
      return;
    }
    return this.unit.toGram(this.amount) / 1000 * this.originalItem.caloriesPerKg;
  }

  // Method just for recipe meal items to fetch their image.

  private calculateFoodEmissions(): number {
    if (this.originalItem instanceof Recipe) {
      return;
    }
    return this.unit.toGram(this.amount) / 1000 * this.originalItem.footprintPerKg * 1000;
  }

  private updateMacrosByValue(carbs: number, fat: number, protein: number): boolean {
    if (this._originalItem instanceof Recipe) {
      return false;
    }
    this._originalItem.carbsPer100g = carbs;
    this._originalItem.proteinPer100g = protein;
    this._originalItem.fatPer100g = fat;
    this._originalItem.carbsKnown = true;
    this._originalItem.proteinKnown = true;
    this._originalItem.fatKnown = true;
    return true;
  }
}

// never send this to backend, as this can't be deserialized
// always send union type IMealFood | IMealFoodProduct which is then deserialized as MealItem
// recipes have to be sent extra, as they can't be deserialized in backend type MealItem
export interface IMealItem {
  amount: number;
  unit: IUnit;
  type: MealItemType;
  readonly originalItem: IFood | IRecipe | IFoodProduct;
}

export enum MealItemType {
  food, recipe, foodProduct
}

export class ChangeMealItem {
  oldItem: MealItem;
  newItem: MealItem;

  constructor(oldItem: MealItem, newItem: MealItem) {
    this.oldItem = oldItem;
    this.newItem = newItem;
  }
}
